// JOBE-0044.4: LibraryRow — one body row in the list. Renders the fixed
// grid (18 / 2.2fr / 96 / 1fr / 100 / 76) per umbrella §R2's column
// inventory. Star cell is the v1 placeholder `·` (favourite write path
// is a future spec).
//
// JOBE-0044.4.1: per umbrella R2's click model, "tap title → inspector
// summary; tap anywhere else → push to full detail screen." Title click
// drives selection (the inspector slot in `.4` is a placeholder and
// `.13` ships the real preview). Every other cell becomes a navigation
// target to `/library/:urn`.
//
// Cells without interactive children (star, kind, owner, updated) wrap
// their content in a <Link> — `<a>` is the right element for both
// pointer and keyboard activation, and the SPA's react-router intercept
// keeps the navigation in-app.
//
// The Where cell carries chip-filter <button>s (project, collection)
// that must keep their existing behaviour: a chip-click appends the
// matching predicate chip rather than navigating. Nesting <button>
// inside <a> is HTML5-invalid, so the Where cell uses a clickable <div>
// with role="link" + tabIndex=0 + onKeyDown for keyboard parity
// (Enter / Space), and the inner chip buttons stopPropagation so a
// chip-click doesn't bubble up to the navigation handler.
//
// URN encoding: `encodeURI` is used so the canonical `urn:jobe:…`
// colons travel verbatim — the `/library/:urn` route accepts the bare
// form (see LibraryDocumentDetail.jsx, which `useParams` reads
// already-decoded).

import { Link, useNavigate } from 'react-router-dom';
import { relTime, urnSlug } from '../lib/relTime.js';

// ownerGlyph: humans → circle, agents → square (3px-radius), departments →
// square with department glyph. Per design handoff §"Result table"
// L166–168. Visual polish in JOBE-0044.7; v1 carries the structural
// distinction via class names + initials.
function ownerInitials(owner) {
  const name = owner?.displayName || owner?.name || urnSlug(owner?.urn || '');
  if (!name) return '–';
  const parts = String(name).split(/[\s_-]+/).filter(Boolean);
  if (parts.length === 0) return name.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function LibraryRow({ row, selected, onSelect, onChipAppend }) {
  const navigate = useNavigate();

  const detailHref = `/library/${encodeURI(row.urn)}`;

  // Title click selects the row (drives the inspector — placeholder in
  // `.4`, real preview in `.13`). This is the R2 tap-title path.
  const onTitleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(row.urn);
  };

  // Where-cell navigation. Uses navigate() rather than an <a> so the
  // inner chip buttons can sit as proper interactive elements without
  // violating HTML5's "no interactive content inside <a>" rule.
  const onWhereClick = () => {
    navigate(detailHref);
  };
  const onWhereKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(detailHref);
    }
  };

  const onProjectClick = (e) => {
    // stopPropagation keeps a chip-click from bubbling into the Where
    // cell's navigation handler.
    e.stopPropagation();
    if (row.project?.urn && onChipAppend) {
      onChipAppend({ friendlyToken: 'project', values: [row.project.urn] });
    }
  };

  const onCollectionClick = (e, c) => {
    e.stopPropagation();
    if (c?.urn && onChipAppend) {
      onChipAppend({ friendlyToken: 'in', values: [c.urn] });
    }
  };

  const cls = ['library-row'];
  if (selected) cls.push('library-row-selected');

  const owner = row.owner;
  const ownerKind = owner?.ownerType || 'person';
  const ownerClass = `library-row-owner-avatar library-row-owner-${ownerKind}`;

  // navLabel describes the destination so screen readers don't announce
  // five generic "link"s on the same row.
  const navLabel = `Open ${row.name || urnSlug(row.urn) || 'document'}`;

  return (
    <li className={cls.join(' ')} data-urn={row.urn}>
      {/* Star — v1 placeholder per R2. Navigates per "tap anywhere
          else → detail". */}
      <Link
        to={detailHref}
        className="library-row-cell-link library-row-star-cell"
        aria-label={`${navLabel} (star)`}
      >
        <span className="library-row-star" aria-hidden="true">·</span>
      </Link>

      {/* Title cell — name + summary + URN. Title-click selects the row
          (drives the inspector); the cell itself is NOT a <Link>. */}
      <div className="library-row-title">
        <button
          type="button"
          className="library-row-title-name"
          onClick={onTitleClick}
        >
          {row.name || urnSlug(row.urn) || 'Untitled'}
        </button>
        {row.summary && (
          <div className="library-row-summary">{row.summary}</div>
        )}
        <div className="library-row-urn" title={row.urn}>{row.urn}</div>
      </div>

      {/* Kind cell — entity-type slug + kindSource. */}
      <Link
        to={detailHref}
        className="library-row-cell-link"
        aria-label={navLabel}
      >
        <div className="library-row-kind">
          <div className="library-row-kind-slug">{(row.kind || '').toUpperCase()}</div>
          <div className="library-row-kind-source">{row.kindSource || ''}</div>
        </div>
      </Link>

      {/* Owner cell — avatar + display name. */}
      <Link
        to={detailHref}
        className="library-row-cell-link"
        aria-label={navLabel}
      >
        <div className="library-row-owner">
          {owner ? (
            <>
              <span className={ownerClass} aria-hidden="true">
                {ownerInitials(owner)}
              </span>
              <span className="library-row-owner-name" title={owner.urn}>
                {owner.displayName || owner.name || urnSlug(owner.urn)}
                {ownerKind === 'agent' && (
                  <span className="library-row-owner-tag">AGENT</span>
                )}
              </span>
            </>
          ) : (
            <span className="muted">—</span>
          )}
        </div>
      </Link>

      {/* Where cell — project + collections. Carries chip-filter
          buttons, so uses a role="link" div with keyboard parity
          instead of <Link> (HTML5 forbids interactive content inside
          <a>). The inner chip buttons stopPropagation so chip-click
          stays on its filter path. */}
      <div
        role="link"
        tabIndex={0}
        className="library-row-cell-link library-row-where-cell"
        aria-label={navLabel}
        onClick={onWhereClick}
        onKeyDown={onWhereKeyDown}
      >
        <div className="library-row-where">
          {row.project ? (
            <button
              type="button"
              className="library-row-where-project"
              onClick={onProjectClick}
              title={row.project.urn}
            >
              {row.project.name || urnSlug(row.project.urn)}
            </button>
          ) : (
            <span className="muted">—</span>
          )}
          {Array.isArray(row.collections) && row.collections.length > 0 && (
            <div className="library-row-where-collections">
              in:{' '}
              {row.collections.map((c, i) => (
                <span key={c.urn || i}>
                  {i > 0 && ', '}
                  <button
                    type="button"
                    className="library-row-where-collection"
                    onClick={(e) => onCollectionClick(e, c)}
                    title={c.urn}
                  >
                    {c.name || urnSlug(c.urn)}
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Updated cell — bold relative time + faint ago-tag. */}
      <Link
        to={detailHref}
        className="library-row-cell-link"
        aria-label={navLabel}
      >
        <div className="library-row-updated" title={row.lastChangedAt || ''}>
          <span className="library-row-updated-rel">{relTime(row.lastChangedAt)}</span>
        </div>
      </Link>
    </li>
  );
}
